export const permissions = {
  general: {
    QUEUE_MONITORING: {
      VIEW: 'ccpro/general/queueMonitoring/queues:view',
      AGENT_ACTIVITY_VIEW: 'ccpro/general/queueMonitoring/agentActivity:view',
      AGENT_ACTIVITY_EDIT: 'ccpro/general/queueMonitoring/agentActivity:edit',
    },
    ACTION_CALL_OPTIONS: {
      CALL_RECORDINGS: {
        EDIT: 'ccpro/general/actionCallOptions/callRecordings:edit',
      },
      OUTBOUND_CALLS: {
        DID_CREATE: 'ccpro/general/actionCallOptions/outboundDIDCalls:create',
        CREATE: 'ccpro/general/actionCallOptions/outboundCalls:create',
      },
      OUTBOUND_TASK_CALLS: {
        CREATE: 'ccpro/general/actionCallOptions/outboundTaskCalls:create',
      },
      CALL_TRANSFER_TO_TECHNICIANS: {
        VIEW: 'ccpro/general/actionCallOptions/callTransferToTechnicians:view',
      },
      CALL_TRANSFER_TO_QUEUE: {
        VIEW: 'ccpro/general/actionCallOptions/callTransferToQueue:view',
      },
      CALL_TRANSFER_TO_ACTIVE_EMPLOYEES: {
        VIEW: 'ccpro/general/actionCallOptions/callTransferToActiveEmployees:view',
      },
      NETWORK_TEST: {
        VIEW: 'ccpro/general/actionCallOptions/networkTest:view',
      },
    },
    VOICE_INTELLIGENCE: {
      CALL_ANALYSIS_VIEW: 'ccpro/general/voiceIntelligence/callAnalysis:view',
      INSIGHTS_VIEW: 'ccpro/general/voiceIntelligence/insights:view',
    },
    // Core permissions rather than ccpro/ ones: the Coaching Hub resolves a
    // viewer's grants against Core in every host, so one grant means the same
    // thing here and in the Monolith. Which tab each one opens is decided
    // inside the MFE; CCPro only asks whether the viewer holds any of them.
    VOICE_INTELLIGENCE_COACHING_HUB: {
      CSR_LEADERBOARD_VIEW:
        'st_core/voice_intelligence/coaching_hub/VoiceIntelligenceCsrLeaderboard:view',
      TEAMS_VIEW:
        'st_core/voice_intelligence/coaching_hub/VoiceIntelligenceTeamsCoachingHub:view',
      ALL_INDIVIDUALS_VIEW:
        'st_core/voice_intelligence/coaching_hub/VoiceIntelligenceAllIndividualsPerformance:view',
      OWN_PERFORMANCE_VIEW:
        'st_core/voice_intelligence/coaching_hub/VoiceIntelligenceOwnPerformanceOnly:view',
    },
    TEXTING: {
      EXECUTE:
        'st_core/customer_communications/chat_messaging/ChatCenterWrite:execute',
    },
    FOLLOW_UPS: {
      ABANDONED_CALLS: {
        EDIT: 'ccpro/general/followUps/abandoned:edit',
      },
      SECOND_CHANCE_LEADS: {
        EDIT: 'ccpro/general/followUps/secondChanceLeads:edit',
      },
      VIRTUAL_AGENT: {
        EDIT: 'ccpro/general/followUps/virtualAgent:edit',
      },
      VOICEMAIL: {
        EDIT: 'ccpro/general/followUps/voicemail:edit',
      },
    },
  },
  manager: {
    CONVERSATION_DETAILS: {
      RECORDING_TRANSCRIPT_SECTION_VIEW: 'ccpro/general/callRecordings:view',
    },
    CONVERSATION_TRANSCRIPTS_AND_CALL_RECORDS_READ:
      'ccpro/general/conversations/transcripts:read',
    CONVERSATION_SENTIMENT: {
      VIEW_OWN: 'ccpro/general/conversations/AI:view',
      EDIT_ALL: 'ccpro/general/conversations/AI/analysis:edit',
    },
    CONVERSATION_OVERVIEW: {
      EDIT_OWN: 'ccpro/general/conversations/historical/user:edit',
      EDIT_ALL: 'ccpro/general/conversations/historical/all:edit',
    },
    MANAGER_ASSIST: {
      VIEW: 'ccpro/admin/managerAssist:view',
    },
  },
  admin: {
    RECORDING_SETTINGS: {
      EDIT: 'ccpro/admin/telecom/recordingSettings:edit',
    },
    VIRTUAL_AGENT: {
      EDIT: 'ccpro/admin/users/virtualAgents:edit',
    },
    VOICE_INTELLIGENCE: {
      SETTINGS_VIEW: 'ccpro/admin/voiceIntelligence/settings:view',
      EXCLUSIONS_EDIT: 'ccpro/admin/voiceIntelligence/exclusions:edit',
    },
    TELECOM_WORKFLOWS: {
      LINES_AND_DEVICES: 'ccpro/admin/telecom/linesAndDevices:edit',
      VIEW: 'ccpro/admin/telecom/workflows:view',
      EDIT: 'ccpro/admin/telecom/workflows:edit',
    },
    USER_MANAGEMENT: {
      VIEW: 'ccpro/admin/users:view',
      QUEUES_EDIT: 'ccpro/admin/users/queues:edit',
      VMBOXES_EDIT: 'ccpro/admin/users/vmBoxes:edit',
      SEAT_LEVELS_EDIT: 'ccpro/admin/users/seatLevels:edit',
    },
    TELECOM_MANAGE_ACCOUNT: {
      EDIT_CALLER_ID: 'admin/telecom/outboundCallerIds:edit',
    },
    FEATURE_MANAGEMENT: {
      VIEW: 'ccpro/admin/featureManagement:view',
      CALL_ANALYSIS_EDIT: 'ccpro/admin/featureManagement/callAnalysis:edit',
      DATA_SHARING_EDIT: 'ccpro/admin/featureManagement/dataSharing:edit',
      INBOX_TASK_CARDS_EDIT: 'ccpro/admin/featureManagement/inboxTaskCard:edit',
      BOOKING_CONFIG_EDIT: 'ccpro/admin/featureManagement/bookingConfig:edit',
    },
    JOB_BOOKING_SETTINGS: {
      ACCESS_SETTINGS: 'st_core/access_admin/general/AccessSettings:execute',
      VIEW_EDIT_JOB_BOOKING: 'st_core/settings/ViewEditJobBooking:execute',
    },
    REPORTS: {
      VOICE_AGENT_DASHBOARD_VIEW:
        'ccpro/admin/reports/voiceAgentDashboard:view',
    },
    CALL_CONTINUITY: {
      FORWARDING_NUMBER_SETTINGS:
        'st_core/access_admin/general/UpdateForwardCallsToNumberOnSettingsPhone:execute',
    },
    PHONE_SETTINGS: {
      EXECUTE:
        'st_core/lead_generation/memberships_campaigns_follow_ups/Phone:edit',
    },
    CONFIGURATION_REVIEW: {
      VIEW: 'ccpro/admin/configurationReview:view',
      ACKNOWLEDGE_ALERTS: 'ccpro/admin/configurationReview/alerts:edit',
    },
  },
} as const;

export const permissionSets = {
  general: {
    QUEUE_MONITORING: Object.values(permissions.general.QUEUE_MONITORING),
    TEXTING: Object.values(permissions.general.TEXTING),
    VOICE_INTELLIGENCE_COACHING_HUB: Object.values(
      permissions.general.VOICE_INTELLIGENCE_COACHING_HUB,
    ),
    FOLLOW_UPS: {
      ABANDONED_CALLS: Object.values(
        permissions.general.FOLLOW_UPS.ABANDONED_CALLS,
      ),
      SECOND_CHANCE_LEADS: Object.values(
        permissions.general.FOLLOW_UPS.SECOND_CHANCE_LEADS,
      ),
      VIRTUAL_AGENT: Object.values(
        permissions.general.FOLLOW_UPS.VIRTUAL_AGENT,
      ),
      VOICEMAIL: Object.values(permissions.general.FOLLOW_UPS.VOICEMAIL),
    },
  },
  manager: {
    CONVERSATION_SENTIMENT: Object.values(
      permissions.manager.CONVERSATION_SENTIMENT,
    ),
    MANAGER_ASSIST: Object.values(permissions.manager.MANAGER_ASSIST),
  },
  admin: {
    RECORDING_SETTINGS: Object.values(permissions.admin.RECORDING_SETTINGS),
    VIRTUAL_AGENT: Object.values(permissions.admin.VIRTUAL_AGENT),
    VOICE_INTELLIGENCE_SETTINGS: Object.values(
      permissions.admin.VOICE_INTELLIGENCE,
    ),
    TELECOM_WORKFLOWS: Object.values(permissions.admin.TELECOM_WORKFLOWS),
    USER_MANAGEMENT: Object.values(permissions.admin.USER_MANAGEMENT),
    TELECOM_MANAGE_ACCOUNT: Object.values(
      permissions.admin.TELECOM_MANAGE_ACCOUNT,
    ),
    FEATURE_MANAGEMENT: Object.values(permissions.admin.FEATURE_MANAGEMENT),
    FEATURE_MANAGEMENT_CALL_ANALYSIS: [
      permissions.admin.FEATURE_MANAGEMENT.VIEW,
      permissions.admin.FEATURE_MANAGEMENT.CALL_ANALYSIS_EDIT,
    ],
    FEATURE_MANAGEMENT_DATA_INTEGRATION: [
      permissions.admin.FEATURE_MANAGEMENT.VIEW,
    ],
    CALL_CONTINUITY_EDIT_NETWORK: [
      permissions.admin.CALL_CONTINUITY.FORWARDING_NUMBER_SETTINGS,
      permissions.admin.PHONE_SETTINGS.EXECUTE,
    ],
    REPORTS: Object.values(permissions.admin.REPORTS),
    PHONE_SETTINGS: Object.values(permissions.admin.PHONE_SETTINGS),
    CONFIGURATION_REVIEW: Object.values(permissions.admin.CONFIGURATION_REVIEW),
    ALL: [
      ...Object.values(permissions.admin.VIRTUAL_AGENT),
      ...Object.values(permissions.admin.VOICE_INTELLIGENCE),
      ...Object.values(permissions.admin.TELECOM_WORKFLOWS),
      ...Object.values(permissions.admin.USER_MANAGEMENT),
      ...Object.values(permissions.admin.TELECOM_MANAGE_ACCOUNT),
      ...Object.values(permissions.admin.RECORDING_SETTINGS),
      ...Object.values(permissions.admin.FEATURE_MANAGEMENT),
      ...Object.values(permissions.admin.CALL_CONTINUITY),
      ...Object.values(permissions.admin.PHONE_SETTINGS),
      ...Object.values(permissions.admin.CONFIGURATION_REVIEW),
    ],
  },
};
